import React, { useState, useEffect } from "react";
import {
  X,
  Edit2,
  Trash2,
  DollarSign,
  Calendar,
  User,
  Building,
  TrendingUp,
  Plus,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const OpportunityDetailsModal = ({
  opportunity,
  onClose,
  onUpdate,
  onDelete,
  users,
}) => {
  const { isDarkMode } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: opportunity.name || "",
    description: opportunity.description || "",
    amount: opportunity.amount || "",
    currency: opportunity.currency || "GBP",
    probability: opportunity.probability || 50,
    expectedCloseDate: opportunity.expectedCloseDate
      ? opportunity.expectedCloseDate.split("T")[0]
      : "",
    stage: opportunity.stage || "qualification",
    status: opportunity.status || "open",
    priority: opportunity.priority || "medium",
    assignedTo: opportunity.assignedTo || "",
    lostReason: opportunity.lostReason || "",
  });
  const [products, setProducts] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    productName: "",
    description: "",
    quantity: 1,
    unitPrice: "",
    discount: 0,
    tax: 0,
  });

  useEffect(() => {
    if (opportunity.products) {
      setProducts(opportunity.products);
    }
  }, [opportunity]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      const updateData = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        probability: formData.probability
          ? parseInt(formData.probability)
          : null,
        expectedCloseDate: formData.expectedCloseDate || null,
        assignedTo: formData.assignedTo || null,
      };

      await onUpdate(opportunity.id, updateData);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating opportunity:", error);
    }
  };

  const handleAddProduct = async () => {
    try {
      const totalPrice =
        newProduct.quantity * newProduct.unitPrice -
        newProduct.discount +
        newProduct.tax;

      const productData = {
        ...newProduct,
        unitPrice: parseFloat(newProduct.unitPrice),
        discount: parseFloat(newProduct.discount) || 0,
        tax: parseFloat(newProduct.tax) || 0,
        totalPrice,
      };

      await apiService.addOpportunityProduct(opportunity.id, productData);

      const updatedOpp = await apiService.getOpportunity(opportunity.id);
      setProducts(updatedOpp.data.products || []);

      setShowAddProduct(false);
      setNewProduct({
        productName: "",
        description: "",
        quantity: 1,
        unitPrice: "",
        discount: 0,
        tax: 0,
      });
    } catch (error) {
      console.error("Error adding product:", error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await apiService.deleteOpportunityProduct(opportunity.id, productId);
        setProducts(products.filter((p) => p.id !== productId));
      } catch (error) {
        console.error("Error deleting product:", error);
      }
    }
  };

  const calculateTotal = () => {
    return products.reduce(
      (sum, product) => sum + parseFloat(product.totalPrice || 0),
      0,
    );
  };

  const formatCurrency = (amount, currency = "GBP") => {
    if (!amount && amount !== 0) return "—";

    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      console.error("Error formatting currency:", error);
      return `${currency || "GBP"} ${parseFloat(amount).toFixed(2)}`;
    }
  };

  const getCurrencySymbol = (currency = "GBP") => {
    const symbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
      CHF: "CHF",
      CNY: "¥",
      INR: "₹",
    };
    return symbols[currency] || currency;
  };

  const formatStage = (stage) => {
    return stage
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const opportunityCurrency = opportunity.currency || "GBP";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl border w-full max-w-4xl max-h-[90vh] overflow-y-auto ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        <div
          className={`sticky top-0 border-b p-6 flex items-center justify-between ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div>
            <h2
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {isEditing ? "Edit Opportunity" : "Opportunity Details"}
            </h2>
            <p
              className={`text-sm mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {opportunity.name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className={`p-2 transition-colors ${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  title="Edit"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => onDelete(opportunity.id)}
                  className={`p-2 transition-colors ${
                    isDarkMode
                      ? "text-gray-400 hover:text-red-400"
                      : "text-gray-500 hover:text-red-500"
                  }`}
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className={`p-2 transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3
              className={`text-base font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Basic Information
            </h3>

            {isEditing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Amount
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Currency
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="JPY">JPY (¥)</option>
                    <option value="AUD">AUD (A$)</option>
                    <option value="CAD">CAD (C$)</option>
                    <option value="CHF">CHF</option>
                    <option value="CNY">CNY (¥)</option>
                    <option value="INR">INR (₹)</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Probability (%)
                  </label>
                  <input
                    type="number"
                    name="probability"
                    value={formData.probability}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Expected Close Date
                  </label>
                  <input
                    type="date"
                    name="expectedCloseDate"
                    value={formData.expectedCloseDate}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Stage
                  </label>
                  <select
                    name="stage"
                    value={formData.stage}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="qualification">Qualification</option>
                    <option value="needs_analysis">Needs Analysis</option>
                    <option value="proposal">Proposal</option>
                    <option value="negotiation">Negotiation</option>
                    <option value="closed_won">Closed Won</option>
                    <option value="closed_lost">Closed Lost</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="open">Open</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Priority
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Assign To
                  </label>
                  <select
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  >
                    <option value="">Select a user</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.status === "lost" && (
                  <div className="md:col-span-2">
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Lost Reason
                    </label>
                    <textarea
                      name="lostReason"
                      value={formData.lostReason}
                      onChange={handleChange}
                      rows={2}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                      }`}
                      placeholder="Why was this opportunity lost?"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                  }`}
                >
                  <div
                    className={`flex items-center gap-2 text-sm mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <DollarSign size={16} />
                    Amount
                  </div>
                  <div
                    className={`text-lg font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {formatCurrency(opportunity.amount, opportunityCurrency)}
                  </div>
                  <div
                    className={`text-xs mt-1 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    {opportunityCurrency}
                  </div>
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                  }`}
                >
                  <div
                    className={`flex items-center gap-2 text-sm mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <TrendingUp size={16} />
                    Probability
                  </div>
                  <div
                    className={`text-lg font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {opportunity.probability}%
                  </div>
                  {opportunity.expectedRevenue && (
                    <div
                      className={`text-xs mt-1 ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Expected:{" "}
                      {formatCurrency(
                        opportunity.expectedRevenue,
                        opportunityCurrency,
                      )}
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                  }`}
                >
                  <div
                    className={`flex items-center gap-2 text-sm mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <Calendar size={16} />
                    Expected Close Date
                  </div>
                  <div
                    className={`text-lg font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {opportunity.expectedCloseDate
                      ? new Date(
                          opportunity.expectedCloseDate,
                        ).toLocaleDateString()
                      : "Not set"}
                  </div>
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                  }`}
                >
                  <div
                    className={`flex items-center gap-2 text-sm mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <User size={16} />
                    Assigned To
                  </div>
                  <div
                    className={`text-lg font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {opportunity.assignedUser?.name || "Unassigned"}
                  </div>
                </div>

                {opportunity.lead && (
                  <div
                    className={`rounded-lg p-4 md:col-span-2 ${
                      isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm mb-1 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      <Building size={16} />
                      Associated Lead
                    </div>
                    <div
                      className={`text-lg font-semibold ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {opportunity.lead.fullName} -{" "}
                      {opportunity.lead.companyName}
                    </div>
                  </div>
                )}

                {opportunity.description && (
                  <div
                    className={`md:col-span-2 rounded-lg p-4 ${
                      isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                    }`}
                  >
                    <div
                      className={`text-sm mb-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Description
                    </div>
                    <div
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      {opportunity.description}
                    </div>
                  </div>
                )}

                {opportunity.lostReason && (
                  <div
                    className={`md:col-span-2 rounded-lg p-4 border ${
                      isDarkMode
                        ? "bg-red-900/20 border-red-500/30"
                        : "bg-red-50 border-red-200"
                    }`}
                  >
                    <div
                      className={`text-sm mb-2 ${
                        isDarkMode ? "text-red-400" : "text-red-600"
                      }`}
                    >
                      Lost Reason
                    </div>
                    <div
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      {opportunity.lostReason}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Products */}
          {!isEditing && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Products
                </h3>
                <button
                  onClick={() => setShowAddProduct(true)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
                    isDarkMode
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-blue-500 hover:bg-blue-600 text-white"
                  }`}
                >
                  <Plus size={16} />
                  Add Product
                </button>
              </div>

              {showAddProduct && (
                <div
                  className={`rounded-lg p-4 border ${
                    isDarkMode
                      ? "bg-gray-800/50 border-gray-700"
                      : "bg-gray-100 border-gray-200"
                  }`}
                >
                  <h4
                    className={`font-medium mb-3 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    New Product
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label
                        className={`block text-sm mb-1 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Product Name
                      </label>
                      <input
                        type="text"
                        value={newProduct.productName}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            productName: e.target.value,
                          })
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                        placeholder="Enter product name"
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm mb-1 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Unit Price ({getCurrencySymbol(opportunityCurrency)})
                      </label>
                      <input
                        type="number"
                        value={newProduct.unitPrice}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            unitPrice: e.target.value,
                          })
                        }
                        step="0.01"
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm mb-1 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Quantity
                      </label>
                      <input
                        type="number"
                        value={newProduct.quantity}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            quantity: parseInt(e.target.value),
                          })
                        }
                        min="1"
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm mb-1 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Discount ({getCurrencySymbol(opportunityCurrency)})
                      </label>
                      <input
                        type="number"
                        value={newProduct.discount}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            discount: e.target.value,
                          })
                        }
                        step="0.01"
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label
                        className={`block text-sm mb-1 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Description
                      </label>
                      <textarea
                        value={newProduct.description}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            description: e.target.value,
                          })
                        }
                        rows={2}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
                            : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                        }`}
                        placeholder="Product description"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleAddProduct}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        isDarkMode
                          ? "bg-blue-600 hover:bg-blue-700 text-white"
                          : "bg-blue-500 hover:bg-blue-600 text-white"
                      }`}
                    >
                      Add Product
                    </button>
                    <button
                      onClick={() => setShowAddProduct(false)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        isDarkMode
                          ? "bg-gray-700 hover:bg-gray-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300 text-gray-900"
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {products.length > 0 ? (
                <div className="space-y-2">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className={`rounded-lg p-4 border ${
                        isDarkMode
                          ? "bg-gray-800/50 border-gray-700"
                          : "bg-gray-100 border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div
                            className={`font-medium ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {product.productName}
                          </div>
                          {product.description && (
                            <div
                              className={`text-sm mt-1 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {product.description}
                            </div>
                          )}
                          <div
                            className={`flex items-center gap-4 mt-2 text-sm ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            <span>
                              Qty:{" "}
                              <span
                                className={
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }
                              >
                                {product.quantity}
                              </span>
                            </span>
                            <span>
                              Unit Price:{" "}
                              <span
                                className={
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }
                              >
                                {formatCurrency(
                                  product.unitPrice,
                                  opportunityCurrency,
                                )}
                              </span>
                            </span>
                            {product.discount > 0 && (
                              <span>
                                Discount:{" "}
                                <span
                                  className={
                                    isDarkMode ? "text-white" : "text-gray-900"
                                  }
                                >
                                  {formatCurrency(
                                    product.discount,
                                    opportunityCurrency,
                                  )}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div
                              className={`font-semibold ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {formatCurrency(
                                product.totalPrice,
                                opportunityCurrency,
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteProduct(product.id)}
                            className={`transition-colors ${
                              isDarkMode
                                ? "text-gray-400 hover:text-red-400"
                                : "text-gray-500 hover:text-red-500"
                            }`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div
                    className={`rounded-lg p-4 border ${
                      isDarkMode
                        ? "bg-blue-900/20 border-blue-500/30"
                        : "bg-blue-50 border-blue-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Total:
                      </span>
                      <span
                        className={`font-bold text-lg ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {formatCurrency(calculateTotal(), opportunityCurrency)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={`text-center py-8 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No products added yet. Click "Add Product" to get started.
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {isEditing && (
            <div
              className={`flex items-center gap-3 pt-4 border-t ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <button
                onClick={() => setIsEditing(false)}
                className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
                    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                }`}
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpportunityDetailsModal;
